import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { SectionHeader, EmptyState, PolicyStatusPill } from "../components/UI";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ClipboardCheck, ScrollText, FileText, Archive } from "lucide-react";
import { TID } from "../constants/testIds";

export default function Approvals() {
  const [policies, setPolicies] = useState([]);
  const [findingsList, setFindingsList] = useState([]);
  const [archives, setArchives] = useState([]);
  const [rejectModal, setRejectModal] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const [pols, casesR, archR] = await Promise.all([
        api.get("/policies", { params: { status: "pending_approval" } }),
        api.get("/cases", { params: { limit: 500 } }),
        api.get("/evidence-archive-requests", { params: { status: "pending" } }),
      ]);
      setPolicies(pols.data);
      setFindingsList(casesR.data.filter((c) => (c.findings)?.status === "pending_approval"));
      setArchives(archR.data);
    } catch (e) { toast.error("Failed to load approvals"); }
  };

  useEffect(() => { load(); }, []);

  const approvePolicy = async (id) => {
    try { await api.post(`/policies/${id}/approve`); toast.success("Policy approved"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const rejectPolicy = async (id, r) => {
    try { await api.post(`/policies/${id}/reject`, { reason: r }); toast.success("Policy rejected"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const approveFindings = async (caseId) => {
    try { await api.post(`/cases/${caseId}/findings/approve`); toast.success("Findings approved"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const rejectFindings = async (caseId, r) => {
    try { await api.post(`/cases/${caseId}/findings/reject`, { reason: r }); toast.success("Findings rejected"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const approveArchive = async (id) => {
    try { await api.post(`/evidence-archive-requests/${id}/approve`); toast.success("Archive approved"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const rejectArchive = async (id, r) => {
    try { await api.post(`/evidence-archive-requests/${id}/reject`, { reason: r }); toast.success("Archive rejected"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const openReject = (type, id) => { setRejectModal({ type, id }); setReason(""); };
  const doReject = async () => {
    if (!reason.trim()) return toast.error("Reason required");
    const { type, id } = rejectModal;
    if (type === "policy") await rejectPolicy(id, reason);
    else if (type === "findings") await rejectFindings(id, reason);
    else await rejectArchive(id, reason);
    setRejectModal(null);
  };

  const total = policies.length + findingsList.length + archives.length;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <SectionHeader
        title="Approvals"
        subtitle={`${total} item${total === 1 ? "" : "s"} require your review.`}
      />

      {total === 0 ? (
        <EmptyState icon={ClipboardCheck} title="Nothing to approve" subtitle="You're all caught up. Approvals will appear here as HR, Managers, or teammates submit workflows."/>
      ) : (
        <div className="space-y-8">
          {findingsList.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <FileText size={14} strokeWidth={2}/>
                <div className="eyebrow">Case Findings ({findingsList.length})</div>
              </div>
              <div className="space-y-3">
                {findingsList.map((c) => (
                  <div key={c.id} className="data-cell p-5">
                    <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                      <div>
                        <div className="text-xs font-mono text-slate-500">{c.case_number}</div>
                        <div className="font-display text-lg font-semibold text-slate-900">{c.title}</div>
                        <div className="text-xs text-slate-500 mt-1">Submitted by {(c.findings)?.submitted_by_name} · Outcome: <span className="font-mono text-slate-900">{(c.findings)?.outcome}</span></div>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/cases/${c.id}`} className="btn-ghost text-xs">Review case</Link>
                        <button className="btn-primary text-xs" onClick={() => approveFindings(c.id)} data-testid={`approve-findings-${c.id}`}><CheckCircle2 size={14}/> Approve</button>
                        <button className="btn-danger text-xs" onClick={() => openReject("findings", c.id)}><XCircle size={14}/> Reject</button>
                      </div>
                    </div>
                    <div className="text-sm text-slate-700 border-l-2 border-slate-200 pl-3 mt-2">{(c.findings)?.decision_summary}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {policies.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ScrollText size={14} strokeWidth={2}/>
                <div className="eyebrow">Policies ({policies.length})</div>
              </div>
              <div className="space-y-3">
                {policies.map((p) => (
                  <div key={p.id} className="data-cell p-5">
                    <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                      <div>
                        <PolicyStatusPill status={p.status}/>
                        <div className="font-display text-lg font-semibold text-slate-900 mt-1.5">{p.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">Submitted by {p.submitted_by_name} · {p.category}</div>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/policies/${p.id}`} className="btn-ghost text-xs">Preview</Link>
                        <button className="btn-primary text-xs" onClick={() => approvePolicy(p.id)} data-testid={`approve-policy-${p.id}`}><CheckCircle2 size={14}/> Approve</button>
                        <button className="btn-danger text-xs" onClick={() => openReject("policy", p.id)}><XCircle size={14}/> Reject</button>
                      </div>
                    </div>
                    {p.summary && <div className="text-sm text-slate-600">{p.summary}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {archives.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Archive size={14} strokeWidth={2}/>
                <div className="eyebrow">Evidence Archive Requests ({archives.length})</div>
              </div>
              <div className="space-y-3">
                {archives.map((r) => (
                  <div key={r.id} className="data-cell p-5">
                    <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                      <div>
                        <div className="font-display text-base font-semibold text-slate-900">{r.evidence_filename}</div>
                        <div className="text-xs text-slate-500 mt-0.5">Requested by {r.requested_by_name} · Case ID {r.case_id?.slice(0, 8)}</div>
                        <div className="text-xs text-slate-700 mt-2">Reason: <span className="italic">{r.reason}</span></div>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/cases/${r.case_id}`} className="btn-ghost text-xs">View case</Link>
                        <button className="btn-primary text-xs" onClick={() => approveArchive(r.id)}><CheckCircle2 size={14}/> Approve archive</button>
                        <button className="btn-danger text-xs" onClick={() => openReject("archive", r.id)}><XCircle size={14}/> Reject</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {rejectModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-300 max-w-md w-full p-6 rounded">
            <h3 className="font-display text-xl font-semibold mb-3">Reject with Reason</h3>
            <p className="text-sm text-slate-500 mb-3">This decision will be logged and the submitter notified.</p>
            <textarea
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain the reason for rejection…"
              className="input mb-4"
              data-testid="reject-reason-input"
            />
            <div className="flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setRejectModal(null)}>Cancel</button>
              <button className="btn-danger" onClick={doReject} data-testid="reject-submit-btn">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
